import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, Printer, Loader2, Edit2, Trash2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SmartCombobox from "@/components/SmartCombobox";
import MultiTagInput from "@/components/MultiTagInput";
import ManageOptionsDialog from "@/components/ManageOptionsDialog";
import StatusBadge from "@/components/StatusBadge";
import CustomerSearchSelect from "@/components/CustomerSearchSelect";

const blank = {
  customer_name: "", mobile: "", email: "",
  device_type: "", brand: "", model_name: "", serial_number: "",
  items_received: [], problems: [], status: "Pending", charge_amount: "",
};

const DEFAULT_WA = "Hello {customer_name},\nYour device {model} (Service ID: *{service_id}*) is *ready to collect*. Total billed amount: Rs. {amount}. Please visit us to collect it. Thank you!";

export default function Services() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [waTemplate, setWaTemplate] = useState(DEFAULT_WA);
  const [store, setStore] = useState({ name: "Digital Yantra" });

  useEffect(() => {
    (async () => {
      try {
        const [wa, st] = await Promise.all([api.get("/settings/whatsapp"), api.get("/settings/store")]);
        setWaTemplate(wa.data.content || DEFAULT_WA);
        setStore(st.data || store);
      } catch (e) { console.error("Failed to load WhatsApp/store settings:", e); }
    })();
    // eslint-disable-next-line
  }, []);

  const buildWaMessage = (s) => (waTemplate || DEFAULT_WA)
    .split("{customer_name}").join(s.customer_name || "")
    .split("{model}").join(`${s.brand || ""} ${s.model_name || ""}`.trim())
    .split("{service_id}").join(s.service_id || "")
    .split("{amount}").join(Number(s.charge_amount || 0).toLocaleString("en-IN"))
    .split("{store_name}").join(store.name || "");

  const normalizeMobile = (m) => {
    const digits = (m || "").replace(/\D/g, "");
    return digits.length === 10 ? `91${digits}` : digits;
  };
  const sendWhatsApp = (s) => {
    const num = normalizeMobile(s.mobile);
    const text = encodeURIComponent(buildWaMessage(s));
    const url = num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener");
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== "ALL") params.status_filter = statusFilter;
      const { data } = await api.get("/services", { params });
      setItems(data);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(fetchItems, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, statusFilter]);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      customer_name: s.customer_name, mobile: s.mobile, email: s.email,
      device_type: s.device_type, brand: s.brand, model_name: s.model_name,
      serial_number: s.serial_number, items_received: s.items_received || [],
      problems: (s.problem_description || "").split(",").map((x) => x.trim()).filter(Boolean),
      status: s.status, charge_amount: s.charge_amount || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_name.trim() || !form.mobile.trim() || form.problems.length === 0) {
      toast.error("Customer, mobile and at least one problem are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/services/${editing.id}`, {
          status: form.status,
          problem_description: form.problems.join(", "),
          charge_amount: Number(form.charge_amount || 0),
        });
        setItems((prev) => prev.map((it) => (it.id === editing.id ? data : it)));
        toast.success("Service updated");
      } else {
        const { data } = await api.post("/services", {
          customer_name: form.customer_name, mobile: form.mobile, email: form.email,
          device_type: form.device_type, brand: form.brand, model_name: form.model_name,
          serial_number: form.serial_number, items_received: form.items_received,
          problem_description: form.problems.join(", "), status: form.status,
          charge_amount: Number(form.charge_amount || 0),
        });
        setItems((prev) => [data, ...prev]);
        toast.success(`Service ${data.service_id} created`);
        window.open(`/services/${data.id}/print`, "_blank", "noopener");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    const prev = items;
    setItems((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try {
      await api.delete(`/services/${id}`);
      toast.success("Service deleted");
    } catch (e) {
      setItems(prev);
      toast.error(formatApiError(e));
    }
  };

  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const toggleSelect = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkConfirm(false);
    if (!ids.length) return;
    const prev = items;
    setItems((p) => p.filter((x) => !selectedIds.has(x.id)));
    setSelectedIds(new Set());
    try {
      const { data } = await api.post("/services/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} ${data.deleted > 1 ? "services" : "service"}`);
    } catch (e) { setItems(prev); toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 03</div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Service Management</h1>
          <p className="text-sm text-[#475569] mt-1">Job sheets, repair tracking and printable dual-copy receipts.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-service-btn">
          <Plus className="w-4 h-4" /> New service entry
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input
            placeholder="Search by service ID, customer, mobile, model…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="services-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 sm:w-44" data-testid="services-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selectedIds.size > 0 && (
        <div>
          <Button variant="outline" onClick={() => setBulkConfirm(true)} className="h-9 gap-2 text-[#EF4444] border-[#EF4444]/40" data-testid="svc-delete-selected">
            <Trash2 className="w-4 h-4" /> Delete selected ({selectedIds.size})
          </Button>
        </div>
      )}

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-[#0055FF] cursor-pointer" data-testid="svc-select-all" aria-label="Select all" />
              </TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Service ID</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Customer</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Device</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Problem</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Created</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-[#64748B]" data-testid="services-empty">
                No service entries yet.
              </TableCell></TableRow>
            ) : items.map((s) => (
              <TableRow key={s.id} data-testid={`service-row-${s.service_id}`} data-state={selectedIds.has(s.id) ? "selected" : undefined}>
                <TableCell onClick={(ev) => ev.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} className="w-4 h-4 accent-[#0055FF] cursor-pointer" data-testid={`svc-select-${s.service_id}`} />
                </TableCell>
                <TableCell className="font-mono text-sm font-medium">{s.service_id}</TableCell>
                <TableCell>
                  <div className="text-sm">{s.customer_name}</div>
                  <div className="font-mono text-[11px] text-[#64748B]">{s.mobile}</div>
                </TableCell>
                <TableCell className="text-sm">
                  <div>{s.brand} {s.model_name}</div>
                  <div className="font-mono text-[11px] text-[#64748B]">{s.device_type}</div>
                </TableCell>
                <TableCell className="text-xs max-w-[260px] truncate" title={s.problem_description}>{s.problem_description}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell className="font-mono text-xs text-[#64748B]">{(s.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => sendWhatsApp(s)} title="Send WhatsApp message" data-testid={`whatsapp-${s.service_id}`}>
                      <MessageCircle className="w-4 h-4 text-[#16A34A]" />
                    </Button>
                    <Link to={`/services/${s.id}/print`} target="_blank" rel="noopener" data-testid={`print-${s.service_id}`}>
                      <Button variant="ghost" size="sm"><Printer className="w-4 h-4 text-[#0055FF]" /></Button>
                    </Link>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-service-${s.service_id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(s.id)} data-testid={`delete-service-${s.service_id}`}>
                      <Trash2 className="w-4 h-4 text-[#EF4444]" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[90vw] xl:max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="service-sheet">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? `Edit ${editing.service_id}` : "New service entry"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Update status or problem(s)." : "Service ID will be auto-generated as SRV-####."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {!editing && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Existing customer (optional)</Label>
                  <CustomerSearchSelect
                    onPick={(c) => setForm((f) => c
                      ? ({ ...f, customer_name: c.name, mobile: c.mobile, email: c.email || "" })
                      : ({ ...f, customer_name: "", mobile: "", email: "" }))}
                    placeholder="Search customers by name or mobile…"
                    testId="svc-customer-picker"
                  />
                  <p className="text-[11px] text-[#64748B]">Pick to auto-fill details, or type below for a new customer.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wide text-[#475569]">Customer Name *</Label>
                    <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="svc-customer-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wide text-[#475569]">Mobile *</Label>
                    <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="font-mono" data-testid="svc-mobile" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wide text-[#475569]">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="svc-email" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between h-7">
                      <Label className="text-xs uppercase tracking-wide text-[#475569]">Device Type</Label>
                      <ManageOptionsDialog optionType="device_types" label="Device Types" testId="manage-device-types" />
                    </div>
                    <SmartCombobox
                      value={form.device_type}
                      onChange={(v) => setForm({ ...form, device_type: v })}
                      optionType="device_types"
                      placeholder="Laptop / Desktop / All-in-One"
                      testId="svc-device-type"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between h-7">
                      <Label className="text-xs uppercase tracking-wide text-[#475569]">Brand</Label>
                      <ManageOptionsDialog optionType="brands" label="Brands" testId="manage-brands" />
                    </div>
                    <SmartCombobox
                      value={form.brand}
                      onChange={(v) => setForm({ ...form, brand: v })}
                      optionType="brands"
                      placeholder="Dell / HP / Lenovo…"
                      testId="svc-brand"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between h-7">
                      <Label className="text-xs uppercase tracking-wide text-[#475569]">Model</Label>
                      <ManageOptionsDialog optionType="models" label="Models" testId="manage-svc-models" />
                    </div>
                    <SmartCombobox
                      value={form.model_name}
                      onChange={(v) => setForm({ ...form, model_name: v })}
                      optionType="models"
                      placeholder="Latitude 7490…"
                      testId="svc-model"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Serial Number</Label></div>
                    <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="font-mono" data-testid="svc-serial" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between h-7">
                    <Label className="text-xs uppercase tracking-wide text-[#475569]">Items Received</Label>
                    <ManageOptionsDialog optionType="items_received" label="Items Received" testId="manage-items-received" />
                  </div>
                  <MultiTagInput
                    values={form.items_received}
                    onChange={(v) => setForm({ ...form, items_received: v })}
                    optionType="items_received"
                    placeholder="Laptop, Charger, Battery…"
                    testId="svc-items"
                  />
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between h-7">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Problem(s) *</Label>
                <ManageOptionsDialog optionType="problems" label="Problems" testId="manage-problems" />
              </div>
              <MultiTagInput
                values={form.problems}
                onChange={(v) => setForm({ ...form, problems: v })}
                optionType="problems"
                placeholder="Select or type a problem…"
                testId="svc-problem"
              />
              <p className="text-[11px] text-[#64748B]">Pick one or more from the list, or type to add a new problem.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="svc-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Billed Amount (₹)</Label>
                <Input type="number" min="0" value={form.charge_amount}
                  onChange={(e) => setForm({ ...form, charge_amount: e.target.value })}
                  placeholder="Final charge" className="font-mono" data-testid="svc-charge-amount" />
              </div>
            </div>
            <p className="text-[11px] text-[#64748B] -mt-3">Billed amount is used in the WhatsApp "ready to collect" message.</p>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="svc-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="svc-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Create & print")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete service entry?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-svc-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} selected service{selectedIds.size > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-svc-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-svc-bulk-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
